import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Field, FieldType, MemberAddeditComponent, MemberAddeditDialogModel } from '../../brokeragency/member-addedit/member-addedit.component';
import { FinanceLedgerService } from '../../../services/finance-ledger/finance-ledger.service';
import { Account, AccountTypeCode, CreateAccountRequest, CreateTransactionRequest } from '../../../services/ledger-client/types';
import { LedgerTransactionDialogComponent } from './ledger-transaction-dialog.component';
import { OpeningBalancesDialogComponent } from './opening-balances-dialog.component';
import { FINANCE_TABS, FinanceTab } from '../finance-tabs';

interface AccountTreeNode {
  account: Account;
  children: AccountTreeNode[];
  isSynthetic?: boolean;
}

interface AccountTypeDefinition {
  code: string;
  name: string;
  normalBalance: string;
  displayOrder: number;
}

interface CreateAccountDialogPayload {
  code?: string;
  name: string;
  accountTypeCode: string;
  parentAccountId?: string;
  description?: string;
  metadata?: Record<string, unknown>;
}

interface EditAccountDialogPayload {
  code?: string;
  name: string;
  parentAccountId?: string;
  description?: string;
  metadata?: Record<string, unknown>;
}

interface CreateAccountContext {
  title?: string;
  presetAccountTypeCode?: string;
  lockAccountType?: boolean;
  presetParentAccountId?: string;
  lockParentAccount?: boolean;
}

@Component({
  selector: 'app-finance-ledger',
  templateUrl: './finance-ledger.component.html',
  styleUrls: ['./finance-ledger.component.scss'],
})
export class FinanceLedgerComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;

  tabs: FinanceTab[] = FINANCE_TABS;
  activeTab: FinanceTab = this.tabs.find((tab) => tab.id === 'ledger') || this.tabs[0];

  accountViewMode: 'tree' | 'list' = 'tree';
  accountStatusFilter: 'all' | 'active' | 'archived' = 'active';

  accountColumnNames = ['id', 'name', 'accountTypeCode', 'normalBalance', 'balance', 'status', 'actionmenu'];
  transactionColumnNames = ['id', 'status', 'source', 'postedAt', 'externalReference'];
  accountItems = new MatTableDataSource<Account>();
  transactionItems = new MatTableDataSource<any>();

  accountTreeControl = new NestedTreeControl<AccountTreeNode>((node) => node.children);
  accountTreeDataSource = new MatTreeNestedDataSource<AccountTreeNode>();

  allAccounts: Account[] = [];
  accountTypes: AccountTypeDefinition[] = [];
  accountBalanceById: Record<string, number> = {};

  asOfDate = '';
  accountIdFilter = '';
  generalLedgerFromDate = '';
  generalLedgerToDate = '';

  private readonly destroy$ = new Subject<void>();

  readonly hasChild = (_: number, node: AccountTreeNode): boolean => node.children.length > 0;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private financeLedgerService: FinanceLedgerService,
  ) {}

  ngOnInit(): void {
    this.setActiveTab();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe(() => this.setActiveTab());

    const now = new Date();
    this.asOfDate = now.toISOString().slice(0, 10);
    this.generalLedgerFromDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
    this.generalLedgerToDate = now.toISOString().slice(0, 10);

    this.reloadAccountTypes();
    this.reloadAll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setActiveTab(): void {
    const parsedUrl = this.router.parseUrl(this.router.url);
    const url = parsedUrl.toString().split('?')[0];
    this.activeTab = this.tabs.find((tab) => tab.link === url) || this.tabs[0];
  }

  gotoTab(tab: FinanceTab): void {
    if (this.activeTab?.id === tab.id) {
      return;
    }
    this.router.navigate([tab.link]).then();
    this.activeTab = tab;
  }

  reloadAll(): void {
    this.reloadAccounts();
    this.reloadTransactions();
    this.loadTrialBalance();
    this.loadGeneralLedger();
  }

  reloadAccountTypes(): void {
    this.financeLedgerService.listAccountTypes().then(accountTypes => {
      this.accountTypes = accountTypes;
      if (this.allAccounts.length) {
        this.rebuildAccountTree(this.allAccounts);
      }
    }).catch((error: unknown) => {
      this.accountTypes = [];
      console.error(error);
    });
  }

  reloadAccounts(): void {
    this.loadAccountsForCurrentFilter().then(accounts => {
      this.allAccounts = accounts;
      this.accountItems.data = accounts;
      this.accountItems.sort = this.sort;
      this.rebuildAccountTree(accounts);
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load accounts'), 'Close', { duration: 3000 });
    });
  }

  onAccountStatusFilterChange(): void {
    this.reloadAccounts();
  }

  reloadTransactions(): void {
    this.financeLedgerService.listTransactions().then((transactions: any[]) => {
      this.transactionItems.data = transactions;
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load transactions'), 'Close', { duration: 3000 });
    });
  }

  loadTrialBalance(): void {
    const asOfDate = this.asOfDate ? new Date(this.asOfDate).toISOString() : undefined;
    this.financeLedgerService.getTrialBalance(asOfDate).then((trialBalance) => {
      this.accountBalanceById = this.extractAccountBalances(trialBalance);
    }).catch((error: unknown) => {
      this.accountBalanceById = {};
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load trial balance'), 'Close', { duration: 3000 });
    });
  }

  loadGeneralLedger(): void {
    const fromDate = this.generalLedgerFromDate ? new Date(this.generalLedgerFromDate).toISOString() : undefined;
    const toDate = this.generalLedgerToDate ? new Date(this.generalLedgerToDate).toISOString() : undefined;

    this.financeLedgerService.getGeneralLedger(
      this.accountIdFilter.trim() || undefined,
      fromDate,
      toDate,
    ).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load general ledger'), 'Close', { duration: 3000 });
    });
  }

  createAccount(): void {
    this.openCreateAccountDialog();
  }

  addAccountAtNode(node: AccountTreeNode): void {
    const context: CreateAccountContext = {
      title: node.isSynthetic ? `Account Under ${node.account.name}` : `Child Account for ${node.account.name}`,
      presetAccountTypeCode: node.account.accountTypeCode,
      lockAccountType: true,
      presetParentAccountId: node.isSynthetic ? '' : node.account.id,
      lockParentAccount: true,
    };

    this.openCreateAccountDialog(context);
  }

  addAccountAtListItem(account: Account): void {
    this.addAccountAtNode({ account, children: [] });
  }

  editAccount(nodeOrAccount: AccountTreeNode | Account): void {
    const account = this.toAccount(nodeOrAccount);
    if (!account || this.isSyntheticAccount(account)) {
      return;
    }

    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel({
        id: account.id,
        code: account.code || '',
        accountTypeCode: account.accountTypeCode,
        name: account.name,
        parentAccountId: account.parentAccountId || '',
        description: account.description || '',
        metadata: account.metadata || {},
      }, this.getEditAccountFields(account), 'Ledger Account', true, 'Edit')
    });

    dialogRef.afterClosed().subscribe((dialogResult: EditAccountDialogPayload | null) => {
      const payload = this.getValidUpdateAccountPayload(dialogResult, account);
      if (!payload) {
        return;
      }

      this.financeLedgerService.updateAccount(account.id, {
        ...payload,
        expectedUpdatedAt: account.updatedAt,
      }).then((updated) => {
        this.reloadAccounts();
        this.snackBar.open(`Account updated: ${updated.name}`, 'Close', { duration: 3000 });
      }).catch((error: unknown) => {
        if (this.financeLedgerService.isConflictError(error)) {
          this.snackBar.open('Account changed by another user. Refresh and try again.', 'Close', { duration: 4000 });
          return;
        }

        this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to update account'), 'Close', { duration: 3000 });
      });
    });
  }

  archiveAccount(nodeOrAccount: AccountTreeNode | Account): void {
    this.setAccountStatus(nodeOrAccount, 'archived');
  }

  restoreAccount(nodeOrAccount: AccountTreeNode | Account): void {
    this.setAccountStatus(nodeOrAccount, 'active');
  }

  canArchiveAccount(nodeOrAccount: AccountTreeNode | Account): boolean {
    const account = this.toAccount(nodeOrAccount);
    if (!account || this.isSyntheticAccount(account)) {
      return false;
    }
    return account.status === 'active';
  }

  canRestoreAccount(nodeOrAccount: AccountTreeNode | Account): boolean {
    const account = this.toAccount(nodeOrAccount);
    if (!account || this.isSyntheticAccount(account)) {
      return false;
    }
    return account.status === 'archived';
  }

  isArchivedAccount(nodeOrAccount: AccountTreeNode | Account): boolean {
    const account = this.toAccount(nodeOrAccount);
    return Boolean(account?.status === 'archived');
  }

  showAccountDetails(nodeOrAccount: AccountTreeNode | Account): void {
    const account = this.toAccount(nodeOrAccount);

    if (!account || this.isSyntheticAccount(account)) {
      return;
    }

    const detailFields: Array<Field> = [
      { name: 'id', label: 'Account ID', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'code', label: 'Code', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'name', label: 'Name', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'accountTypeCode', label: 'Account Type', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'normalBalance', label: 'Normal Balance', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'parentAccountId', label: 'Parent Account ID', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'status', label: 'Status', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'isSystem', label: 'System Account', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'isRoot', label: 'Root Account', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'updatedAt', label: 'Updated At', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'description', label: 'Description', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON, readOnly: true },
    ];

    const details = {
      id: account.id,
      code: account.code,
      name: account.name,
      accountTypeCode: account.accountTypeCode,
      normalBalance: account.normalBalance,
      parentAccountId: account.parentAccountId || '',
      status: account.status,
      isSystem: String(account.isSystem),
      isRoot: String(account.isRoot),
      updatedAt: account.updatedAt,
      description: account.description || '',
      metadata: account.metadata || {},
    };

    this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(details, detailFields, 'Ledger Account Details', false, 'View'),
      autoFocus: false,
    });
  }

  createTransaction(): void {
    const dialogRef = this.dialog.open(LedgerTransactionDialogComponent, {
      width: '960px',
      maxWidth: '96vw',
      data: {
        accounts: this.allAccounts,
      },
    });

    dialogRef.afterClosed().subscribe((payload: CreateTransactionRequest | null | undefined) => {
      if (!payload) {
        return;
      }

      this.financeLedgerService.createTransaction(payload).then(() => {
        this.reloadTransactions();
        this.loadTrialBalance();
        this.loadGeneralLedger();
        this.snackBar.open('Transaction created', 'Close', { duration: 3000 });
      }).catch((error: unknown) => {
        this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to create transaction'), 'Close', { duration: 3000 });
      });
    });
  }

  setOpeningBalances(): void {
    const dialogRef = this.dialog.open(OpeningBalancesDialogComponent, {
      width: '960px',
      maxWidth: '96vw',
      data: {
        accounts: this.allAccounts,
      },
    });

    dialogRef.afterClosed().subscribe((payload: CreateTransactionRequest | null | undefined) => {
      if (!payload) {
        return;
      }

      this.financeLedgerService.createTransaction(payload).then(() => {
        this.reloadTransactions();
        this.loadTrialBalance();
        this.loadGeneralLedger();
        this.snackBar.open('Opening balances posted', 'Close', { duration: 3000 });
      }).catch((error: unknown) => {
        this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to set opening balances'), 'Close', { duration: 3000 });
      });
    });
  }

  selectAccountFromTree(node: AccountTreeNode): void {
    if (node.isSynthetic) {
      this.accountIdFilter = '';
      this.loadGeneralLedger();
      return;
    }

    this.accountIdFilter = node.account.id;
    this.loadGeneralLedger();
  }

  isSyntheticNode(node: AccountTreeNode | null | undefined): boolean {
    return Boolean(node?.isSynthetic);
  }

  getAccountBalance(account: Account): number | null {
    const value = this.accountBalanceById[account.id];
    if (typeof value === 'number') {
      return value;
    }
    return null;
  }

  getNodeBalance(node: AccountTreeNode): number | null {
    if (!node.isSynthetic) {
      return this.getAccountBalance(node.account);
    }

    const childBalances = node.children
      .map((childNode) => this.getNodeBalance(childNode))
      .filter((balance): balance is number => typeof balance === 'number');

    if (!childBalances.length) {
      return null;
    }

    return Math.round((childBalances.reduce((sum, value) => sum + value, 0) + Number.EPSILON) * 100) / 100;
  }

  private openCreateAccountDialog(context: CreateAccountContext = {}): void {
    this.ensureAccountTypesLoaded().then(() => {
      const dialogRef = this.dialog.open(MemberAddeditComponent, {
        data: new MemberAddeditDialogModel({
          metadata: {},
          accountTypeCode: context.presetAccountTypeCode || undefined,
          parentAccountId: context.presetParentAccountId !== undefined ? context.presetParentAccountId : undefined,
        }, this.getCreateAccountFields(context), context.title || 'Ledger Account', true, 'Add')
      });

      dialogRef.afterClosed().subscribe((dialogResult: CreateAccountDialogPayload | null) => {
        const resolvedResult = dialogResult ? {
          ...dialogResult,
          accountTypeCode: dialogResult.accountTypeCode || context.presetAccountTypeCode || '',
          parentAccountId: dialogResult.parentAccountId !== undefined
            ? dialogResult.parentAccountId
            : context.presetParentAccountId,
        } : null;

        const payload = this.getValidAccountPayload(resolvedResult);
        if (!payload) {
          return;
        }

        this.financeLedgerService.createAccount(payload).then((account) => {
          this.reloadAccounts();
          this.loadTrialBalance();
          this.snackBar.open(`Account created: ${account.name}`, 'Close', { duration: 3000 });
        }).catch((error: unknown) => {
          this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to create account'), 'Close', { duration: 3000 });
        });
      });
    });
  }

  private ensureAccountTypesLoaded(): Promise<void> {
    if (this.accountTypes.length) {
      return Promise.resolve();
    }

    return this.financeLedgerService.listAccountTypes().then((accountTypes) => {
      this.accountTypes = accountTypes;
    }).catch((error: unknown) => {
      this.accountTypes = [];
      console.error(error);
    });
  }

  private getCreateAccountFields(context: CreateAccountContext = {}): Array<Field> {
    const accountTypeValues = this.getAccountTypeDropdownValues();
    const parentAccountValues = [
      'No Parent:',
      ...this.getParentAccountDropdownValues(context.presetAccountTypeCode),
    ];

    return [
      { name: 'code', label: 'Code (optional)', fieldType: FieldType.F_STRING },
      { name: 'name', label: 'Name', fieldType: FieldType.F_STRING, required: true },
      {
        name: 'accountTypeCode',
        label: 'Account Type',
        fieldType: FieldType.F_DROPDOWN,
        values: accountTypeValues,
        required: true,
        readOnly: Boolean(context.lockAccountType),
      },
      {
        name: 'parentAccountId',
        label: 'Parent Account',
        fieldType: FieldType.F_DROPDOWN,
        values: parentAccountValues,
        readOnly: Boolean(context.lockParentAccount),
      },
      { name: 'description', label: 'Description (optional)', fieldType: FieldType.F_STRING },
      { name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON },
    ];
  }

  private getEditAccountFields(account: Account): Array<Field> {
    const parentAccountValues = [
      'No Parent:',
      ...this.getParentAccountDropdownValues(account.accountTypeCode, account.id),
    ];

    return [
      { name: 'id', label: 'Account ID', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'code', label: 'Code', fieldType: FieldType.F_STRING },
      { name: 'accountTypeCode', label: 'Account Type', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'name', label: 'Name', fieldType: FieldType.F_STRING, required: true },
      {
        name: 'parentAccountId',
        label: 'Parent Account',
        fieldType: FieldType.F_DROPDOWN,
        values: parentAccountValues,
      },
      { name: 'description', label: 'Description (optional)', fieldType: FieldType.F_STRING },
      { name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON },
    ];
  }

  private getAccountTypeDropdownValues(): string[] {
    if (this.accountTypes.length) {
      return this.accountTypes
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map(accountType => `${this.cleanLabel(accountType.name)} (${accountType.code}):${accountType.code}`);
    }

    const fromAccounts = Array.from(new Set(this.allAccounts.map(account => account.accountTypeCode))).filter(Boolean);
    if (fromAccounts.length) {
      return fromAccounts.map(accountTypeCode => `${accountTypeCode}:${accountTypeCode}`);
    }

    return [
      'assets:assets',
      'liabilities:liabilities',
      'revenue:revenue',
      'expenses:expenses',
    ];
  }

  private getParentAccountDropdownValues(accountTypeCode?: string, excludedAccountId?: string): string[] {
    return this.allAccounts
      .filter(account => !accountTypeCode || account.accountTypeCode === accountTypeCode)
      .filter(account => !excludedAccountId || account.id !== excludedAccountId)
      .slice()
      .sort((account1, account2) => this.compareAccounts(account1, account2))
      .map(account => {
        const left = this.cleanLabel(account.code || account.id);
        const right = this.cleanLabel(account.name);
        return `${left} ${right}:${account.id}`;
      });
  }

  private getValidAccountPayload(payload: CreateAccountDialogPayload | null): CreateAccountRequest | null {
    if (!payload) {
      return null;
    }

    const name = String(payload.name || '').trim();
    if (!name) {
      this.snackBar.open('name is required', 'Close', { duration: 3000 });
      return null;
    }

    const accountTypeCode = String(payload.accountTypeCode || '').trim();
    if (!accountTypeCode) {
      this.snackBar.open('accountTypeCode is required', 'Close', { duration: 3000 });
      return null;
    }

    if (payload.metadata !== undefined && (typeof payload.metadata !== 'object' || payload.metadata === null || Array.isArray(payload.metadata))) {
      this.snackBar.open('metadata must be a JSON object when provided', 'Close', { duration: 3000 });
      return null;
    }

    const code = String(payload.code || '').trim();
    const description = String(payload.description || '').trim();
    const parentAccountId = String(payload.parentAccountId || '').trim();

    const createPayload: CreateAccountRequest = {
      name,
      accountTypeCode: accountTypeCode as AccountTypeCode,
      code: code || undefined,
      description: description || undefined,
      parentAccountId: parentAccountId || undefined,
      metadata: payload.metadata || undefined,
    };

    return createPayload;
  }

  private getValidUpdateAccountPayload(payload: EditAccountDialogPayload | null, account: Account): {
    code?: string;
    name?: string;
    parentAccountId?: string | null;
    description?: string;
    metadata?: Record<string, unknown>;
  } | null {
    if (!payload) {
      return null;
    }

    const name = String(payload.name || '').trim();
    if (!name) {
      this.snackBar.open('name is required', 'Close', { duration: 3000 });
      return null;
    }

    if (payload.metadata !== undefined && (typeof payload.metadata !== 'object' || payload.metadata === null || Array.isArray(payload.metadata))) {
      this.snackBar.open('metadata must be a JSON object when provided', 'Close', { duration: 3000 });
      return null;
    }

    const parentAccountId = String(payload.parentAccountId || '').trim();
    if (parentAccountId && parentAccountId === account.id) {
      this.snackBar.open('parentAccountId cannot equal account id', 'Close', { duration: 3000 });
      return null;
    }

    const description = String(payload.description || '').trim();
    const code = String(payload.code || '').trim();
    return {
      code: code || undefined,
      name,
      parentAccountId: parentAccountId || null,
      description: description || undefined,
      metadata: payload.metadata || undefined,
    };
  }

  private extractAccountBalances(trialBalance: unknown): Record<string, number> {
    const balances: Record<string, number> = {};
    const lines = (trialBalance as any)?.lines;
    if (!Array.isArray(lines)) {
      return balances;
    }

    lines.forEach((line) => {
      const accountId = String(line?.accountId || '').trim();
      const balanceValue = Number(line?.balance);
      if (!accountId || !Number.isFinite(balanceValue)) {
        return;
      }
      balances[accountId] = Math.round((balanceValue + Number.EPSILON) * 100) / 100;
    });

    return balances;
  }

  private rebuildAccountTree(accounts: Account[]): void {
    const nodesById = new Map<string, AccountTreeNode>();
    accounts.forEach(account => {
      nodesById.set(account.id, {
        account,
        children: [],
      });
    });

    const roots: AccountTreeNode[] = [];
    const explicitRootIds = new Set(
      accounts
        .filter(account => account.isRoot || !account.parentAccountId)
        .map(account => account.id),
    );

    const rootByAccountType = new Map<string, AccountTreeNode>();
    explicitRootIds.forEach(rootId => {
      const rootNode = nodesById.get(rootId);
      if (rootNode) {
        roots.push(rootNode);
        if (!rootByAccountType.has(rootNode.account.accountTypeCode)) {
          rootByAccountType.set(rootNode.account.accountTypeCode, rootNode);
        }
      }
    });

    this.accountTypes.forEach((accountType) => {
      if (!rootByAccountType.has(accountType.code)) {
        const syntheticRoot: AccountTreeNode = {
          account: this.createSyntheticRootAccount(accountType),
          children: [],
          isSynthetic: true,
        };
        roots.push(syntheticRoot);
        rootByAccountType.set(accountType.code, syntheticRoot);
      }
    });

    nodesById.forEach(node => {
      if (explicitRootIds.has(node.account.id)) {
        return;
      }

      const parentId = node.account.parentAccountId;
      const parentNode = parentId ? nodesById.get(parentId) : undefined;
      if (parentNode && parentId !== node.account.id) {
        parentNode.children.push(node);
        return;
      }

      const accountTypeRoot = rootByAccountType.get(node.account.accountTypeCode);
      if (accountTypeRoot) {
        accountTypeRoot.children.push(node);
        return;
      }

      roots.push(node);
    });

    this.sortTreeNodes(roots);
    this.accountTreeDataSource.data = roots;
  }

  private createSyntheticRootAccount(accountType: AccountTypeDefinition): Account {
    return {
      id: `root:${accountType.code}`,
      code: accountType.code.toUpperCase(),
      name: accountType.name,
      accountTypeCode: accountType.code as AccountTypeCode,
      normalBalance: accountType.normalBalance as 'debit' | 'credit',
      parentAccountId: null,
      description: null,
      metadata: null,
      isSystem: true,
      isRoot: true,
      status: 'active',
      updatedAt: new Date(0).toISOString(),
    };
  }

  private sortTreeNodes(nodes: AccountTreeNode[]): void {
    nodes.sort((node1, node2) => this.compareAccounts(node1.account, node2.account));
    nodes.forEach(node => this.sortTreeNodes(node.children));
  }

  private compareAccounts(account1: Account, account2: Account): number {
    const value1 = `${account1.code || ''} ${account1.name || account1.id}`.toLowerCase();
    const value2 = `${account2.code || ''} ${account2.name || account2.id}`.toLowerCase();
    if (value1 > value2) {
      return 1;
    }
    if (value1 < value2) {
      return -1;
    }
    return 0;
  }

  private cleanLabel(value: string): string {
    return value.replace(/:/g, '').trim();
  }

  private loadAccountsForCurrentFilter(): Promise<Account[]> {
    if (this.accountStatusFilter === 'all') {
      return Promise.all([
        this.financeLedgerService.listAccounts('active'),
        this.financeLedgerService.listAccounts('archived'),
      ]).then(([activeAccounts, archivedAccounts]) => {
        const accountById = new Map<string, Account>();
        [...activeAccounts, ...archivedAccounts].forEach((account) => {
          accountById.set(account.id, account);
        });
        return Array.from(accountById.values());
      });
    }

    return this.financeLedgerService.listAccounts(this.accountStatusFilter);
  }

  private setAccountStatus(nodeOrAccount: AccountTreeNode | Account, status: 'active' | 'archived'): void {
    const account = this.toAccount(nodeOrAccount);
    if (!account || this.isSyntheticAccount(account)) {
      return;
    }

    if (account.status === status) {
      return;
    }

    this.financeLedgerService.updateAccountStatus(account.id, status).then((updatedAccount) => {
      if (this.accountIdFilter === account.id && updatedAccount.status === 'archived' && this.accountStatusFilter === 'active') {
        this.accountIdFilter = '';
      }

      this.reloadAccounts();
      this.loadTrialBalance();
      this.loadGeneralLedger();
      this.snackBar.open(
        `Account ${updatedAccount.status === 'archived' ? 'archived' : 'restored'}: ${updatedAccount.name}`,
        'Close',
        { duration: 3000 },
      );
    }).catch((error: unknown) => {
      const fallbackMessage = status === 'archived' ? 'Unable to archive account' : 'Unable to restore account';
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, fallbackMessage), 'Close', { duration: 3000 });
    });
  }

  private toAccount(nodeOrAccount: AccountTreeNode | Account | null | undefined): Account | null {
    if (!nodeOrAccount) {
      return null;
    }

    if ((nodeOrAccount as AccountTreeNode).account) {
      return (nodeOrAccount as AccountTreeNode).account;
    }
    return nodeOrAccount as Account;
  }

  private isSyntheticAccount(account: Account): boolean {
    return account.id.startsWith('root:');
  }
}
